import React, { startTransition } from 'react';
import { hydrateRoot }  from 'react-dom/client';
import { ApolloProvider } from '@apollo/client';
import App from './App';
import {BrowserRouter} from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { loadableReady } from '@loadable/component';
import { createClient }  from './graphql/client'

// Wyłączenie logów w trybie produkcyjnym
const isDevelopment = process.env.NODE_ENV !== 'production';
if (!isDevelopment) {
    console.log = () => {};
    console.warn = () => {};
    // console.error = () => {}; // Opcjonalnie zostaw console.error dla krytycznych błędów
}


// Dodajemy obsługę hot reload
if (module.hot ) {
  console.log('HMR aktywny - aplikacja będzie automatycznie odświeżać się po zmianach kodu');

  // Wymuszamy pełne przeładowanie strony po aktualizacji
  module.hot.accept('./App', () => {
    console.log('App module updated - reloading');
    window.location.reload();
  });

  // Nasłuchujemy na wszystkie aktualizacje
  module.hot.accept();

  // Obsługa błędów
  module.hot.addStatusHandler(status => {
    if (status === 'abort' || status === 'fail') {
      console.log('HMR aktualizacja nieudana, przeładowuję stronę...');
      window.location.reload();
    }
  });

  console.log(`💻 Client initialized at ${new Date().toLocaleTimeString()} - HMR READY`);
}

const csrOnly = window.__CSR_ONLY__ === true;
// Initialize Apollo Client with the cached data from the server
const apolloClient = createClient(false, window.__APOLLO_STATE__);
console.log('CLIENT JS LOADED', new Date());
const helmetContext = {};
// Główna aplikacja
const AppTree = (
    <HelmetProvider context={helmetContext}>
        <ApolloProvider client={apolloClient}>
            <BrowserRouter>
                <App />
            </BrowserRouter>
        </ApolloProvider>
    </HelmetProvider>
);

// Element kontenera
const container = document.getElementById('root');
if (!container) {
    throw new Error('Nie znaleziono elementu #root w HTML!');
}

// Obsługa hydratacji z lepszym zarządzaniem błędami
if (csrOnly) {
    // Tryb tylko po stronie klienta (CSR) bez próby hydratacji
    console.log('🎯 Using client-side rendering mode (CSR)');
    const { createRoot } = require('react-dom/client');
    const root = createRoot(container);
    root.render(AppTree);
} else {
    // Standardowa ścieżka z hydratacją zawartości wyrenderowanej na serwerze
    console.log('🚀 Using hydration for server-rendered content');
    
    // Ustawiamy flagi dla lepszego śledzenia
    window.__HYDRATION_ATTEMPTED = true;
    
    // Timing dla deweloperów
    const hydrationStart = isDevelopment ? performance.now() : null;

    // Opóźniamy aktualizacje do zakończenia hydracji
    let isHydrating = true;
    
    // Wrapper dla wszystkich setState/aktualizacji podczas hydracji
    const originalSetTimeout = window.setTimeout;
    const originalSetInterval = window.setInterval;
    const originalRequestAnimationFrame = window.requestAnimationFrame;
    const deferredUpdates = [];
    
    // Globalny wrapper dla odroczenia aktualizacji
    const deferUpdate = (fn) => {
        if (isHydrating) {
            deferredUpdates.push(fn);
            return -1;
        }
        return fn();
    };
    
    // Wrapper dla setTimeout
    window.setTimeout = (fn, delay, ...args) => {
        if (isHydrating && delay === 0) {
            return deferUpdate(() => fn(...args));
        }
        return originalSetTimeout(fn, delay, ...args);
    };
    
    // Wrapper dla setInterval
    window.setInterval = (fn, delay, ...args) => {
        if (isHydrating) {
            return deferUpdate(() => originalSetInterval(fn, delay, ...args));
        }
        return originalSetInterval(fn, delay, ...args);
    };
    
    // Wrapper dla requestAnimationFrame
    window.requestAnimationFrame = (fn) => {
        if (isHydrating) {
            return deferUpdate(() => originalRequestAnimationFrame(fn));
        }
        return originalRequestAnimationFrame(fn);
    };
    
    // Flaga hydracji dla globalnego dostępu
    window.__IS_HYDRATING = true;

    // Czekamy na załadowanie zasobów przed próbą hydratacji
    loadableReady(() => {
        if (isDevelopment) {
            console.log('📦 Loadable components ready, starting hydration...');
            console.log('🔍 Apollo state:', window.__APOLLO_STATE__ ? 'Present' : 'Missing');
            console.log('🌐 DOM content:', container.innerHTML ? 'Present' : 'Empty');
        }
        
        try {
            // Używamy hydrateRoot z obsługą błędów i startTransition
            const root = hydrateRoot(container, AppTree, {
                onRecoverableError: (error, errorInfo) => {
                    if (isDevelopment) {
                        console.warn('⚠️ Recovered from hydration error:', error.message);
                        
                        // Specjalne logowanie dla błędów Suspense
                        if (error.message.includes('Suspense boundary')) {
                            console.log('🔄 Suspense boundary error - deferring updates');
                            console.log('📍 Error origin:', errorInfo.digest);
                        }
                        
                        console.log('📋 Error details:', errorInfo);
                        console.log('🔧 Component stack:', errorInfo.componentStack);
                    }
                },
                // Ustawiamy identifierPrefix dla lepszego śledzenia
                identifierPrefix: isDevelopment ? 'falcon-dev-' : 'falcon-'
            });
            
            // Przywracamy oryginalne funkcje i wykonujemy odroczone aktualizacje
            window.setTimeout = originalSetTimeout;
            window.setInterval = originalSetInterval;
            window.requestAnimationFrame = originalRequestAnimationFrame;
            window.__IS_HYDRATING = false;
            isHydrating = false;
            
            // Wykonujemy wszystkie odroczone aktualizacje w startTransition
            if (deferredUpdates.length > 0) {
                startTransition(() => {
                    if (isDevelopment) {
                        console.log(`🔄 Executing ${deferredUpdates.length} deferred updates`);
                    }
                    deferredUpdates.forEach(update => update());
                });
            }
            
            // Opakowujemy operacje po hydracji w startTransition
            startTransition(() => {
                if (isDevelopment) {
                    const hydrationTime = performance.now() - hydrationStart;
                    console.log(`✅ Hydration completed successfully in ${hydrationTime.toFixed(2)}ms`);
                }
                window.__HYDRATION_SUCCEEDED = true;
            });
        } catch (error) {
            if (isDevelopment) {
                console.error('❌ Hydration failed, falling back to client-side rendering:', error);
                console.log('🔍 Error details:', {
                    message: error.message,
                    stack: error.stack,
                    containerContent: container.innerHTML.substring(0, 200) + '...'
                });
            }
            
            // Oznaczamy, że hydratacja nie powiodła się
            window.__HYDRATION_SUCCEEDED = false;
            
            // Usuwamy zawartość kontenera, aby uniknąć problemów z dublowaniem treści
            while (container.firstChild) {
                container.removeChild(container.firstChild);
            }
            
            // Przełączamy się na pełne renderowanie po stronie klienta
            const { createRoot } = require('react-dom/client');
            const root = createRoot(container);
            root.render(AppTree);
            
            if (isDevelopment) {
                console.log('🔄 Fallback to CSR completed');
            }
        }
    });
}